<template>
  <transition name="modal">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      @click.self="closeModal"
    >
      <div
        class="relative w-full max-w-lg bg-white border border-gray-200 rounded-3xl shadow-2xl overflow-hidden"
        @click.stop
      >
        <!-- Header -->
        <div class="px-6 py-4 border-b border-gray-200">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div class="p-2 bg-blue-100 rounded-lg">
                <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                </svg>
              </div>
              <div>
                <h3 class="text-xl font-bold text-gray-900">Resend Email</h3>
                <p class="text-sm text-gray-600">Update email address and resend notification</p>
              </div>
            </div>
            <button
              @click="closeModal"
              class="p-2 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Body -->
        <div class="px-6 py-6 space-y-5">
          <!-- Customer Info -->
          <div class="p-4 bg-gray-50 rounded-xl border border-gray-200">
            <div class="flex items-center gap-3">
              <div class="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center ring-2 ring-gray-300">
                <span class="text-white text-lg font-semibold">
                  {{ (customerName || 'U').charAt(0).toUpperCase() }}
                </span>
              </div>
              <div>
                <p class="text-base font-semibold text-gray-900">{{ customerName || 'Unknown' }}</p>
                <p class="text-sm text-gray-600">Entry ID: {{ entryId }}</p>
              </div>
            </div>
          </div>

          <!-- Bounce Details - Prominent Display -->
          <div v-if="bounceClassification || bounceReason" class="space-y-3">
            <!-- Bounce Classification -->
            <div v-if="bounceClassification" class="p-4 bg-red-50 border-2 border-red-300 rounded-xl">
              <p class="text-xs font-semibold text-red-700 uppercase tracking-wide mb-2">Bounce Type</p>
              <p class="text-base font-bold text-gray-900">{{ bounceClassification }}</p>
            </div>
            
            <!-- Detailed Reason -->
            <div v-if="bounceReason" class="p-4 bg-red-50 border-2 border-red-300 rounded-xl">
              <p class="text-xs font-semibold text-red-700 uppercase tracking-wide mb-2">Why It Bounced</p>
              <p class="text-sm text-gray-900 leading-relaxed break-words font-medium">{{ bounceReason }}</p>
            </div>
          </div>

          <!-- Email Input -->
          <div>
            <label for="email-input" class="block text-sm font-medium text-gray-900 mb-2">
              Email Address
            </label>
            <input
              id="email-input"
              v-model="emailAddress"
              type="email"
              class="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 font-medium placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm"
              placeholder="customer@example.com"
              @keyup.enter="handleResend"
            />
            <p v-if="emailError" class="mt-2 text-xs text-red-600">{{ emailError }}</p>
            <p v-else class="mt-2 text-xs text-gray-500">
              Update the email address if needed, then click resend
            </p>
          </div>

          <!-- Info Note -->
          <div class="p-4 bg-blue-50 border-2 border-blue-200 rounded-xl">
            <div class="flex gap-3">
              <svg class="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
              </svg>
              <div class="flex-1">
                <p class="text-sm font-semibold text-gray-900">What happens next?</p>
                <ul class="mt-2 text-sm text-gray-700 space-y-1">
                  <li>• The notification will be resent to the email address above</li>
                  <li>• If you changed the email, the form entry will be updated</li>
                  <li>• You'll receive a confirmation when the email is sent</li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Error Message -->
          <div v-if="error" class="p-4 bg-red-50 border-2 border-red-300 rounded-xl">
            <div class="flex gap-3">
              <svg class="w-5 h-5 text-red-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
              </svg>
              <p class="text-sm font-medium text-gray-900">{{ error }}</p>
            </div>
          </div>

          <!-- Success Message -->
          <div v-if="success" class="p-4 bg-green-50 border-2 border-green-300 rounded-xl">
            <div class="flex gap-3">
              <svg class="w-5 h-5 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
              </svg>
              <p class="text-sm font-medium text-gray-900">{{ success }}</p>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            @click="closeModal"
            class="px-5 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 transition-all duration-200"
            :disabled="isResending"
          >
            Cancel
          </button>
          <button
            @click="handleResend"
            class="px-5 py-2.5 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white transition-all duration-200 shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            :disabled="isResending || !isValidEmail"
          >
            <svg
              v-if="isResending"
              class="w-4 h-4 animate-spin"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
            </svg>
            {{ isResending ? 'Sending...' : 'Resend Email' }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ResendEmailModal',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    tracking: {
      type: Object,
      default: null
    },
    customerName: {
      type: String,
      default: ''
    },
    formId: {
      type: [String, Number],
      default: null
    },
    entryId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      emailAddress: '',
      isResending: false,
      error: null,
      success: null,
      emailError: null
    };
  },
  computed: {
    bounceClassification() {
      return this.tracking?.bounce_classification || null;
    },
    bounceReason() {
      return this.tracking?.bounce_reason || null;
    },
    isValidEmail() {
      if (!this.emailAddress) return false;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(this.emailAddress);
    }
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        // Reset state when modal opens
        this.emailAddress = this.tracking?.email || '';
        this.error = null;
        this.success = null;
        this.emailError = null;
        this.isResending = false;
      }
    },
    emailAddress() {
      // Clear email error when user types
      this.emailError = null;
    }
  },
  methods: {
    closeModal() {
      if (!this.isResending) {
        this.$emit('close');
      }
    },
    
    async handleResend() {
      // Validate email
      if (!this.isValidEmail) {
        this.emailError = 'Please enter a valid email address';
        return;
      }
      
      if (!this.formId || !this.entryId) {
        this.error = 'Missing form or entry information';
        return;
      }
      
      this.isResending = true;
      this.error = null;
      this.success = null;
      
      try {
        console.log(`🔄 Resending notification for entry ${this.entryId}...`);
        
        const response = await axios.post('/.netlify/functions/resend-notification', {
          formId: this.formId,
          entryId: this.entryId,
          newEmail: this.emailAddress !== this.tracking?.email ? this.emailAddress : null
        });
        
        if (response.data.success) {
          this.success = 'Email notification sent successfully! Refreshing status...';
          console.log('✅ Notification resent successfully');
          
          // Wait 1.5 seconds to show success message, then emit and close
          setTimeout(() => {
            this.$emit('resent', {
              entryId: this.entryId,
              newEmail: this.emailAddress
            });
            this.closeModal();
          }, 1500);
        } else {
          this.error = response.data.error || 'Failed to resend notification';
        }
      } catch (error) {
        console.error('Error resending notification:', error);
        this.error = error.response?.data?.error || error.response?.data?.message || 'Failed to resend notification. Please try again.';
      } finally {
        this.isResending = false;
      }
    }
  }
};
</script>

<style scoped>
/* Modal transition animations */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-active > div,
.modal-leave-active > div {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from > div,
.modal-leave-to > div {
  transform: scale(0.9);
  opacity: 0;
}

/* Input styling */
input[type="email"] {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  -webkit-text-fill-color: #111827; /* Ensure text is dark gray/black */
  opacity: 1;
}

input[type="email"]:focus {
  outline: none;
  -webkit-text-fill-color: #111827;
}

input[type="email"]::placeholder {
  color: #6b7280;
  opacity: 1;
}

/* Ensure autofill doesn't override styles */
input[type="email"]:-webkit-autofill,
input[type="email"]:-webkit-autofill:hover,
input[type="email"]:-webkit-autofill:focus {
  -webkit-text-fill-color: #111827;
  -webkit-box-shadow: 0 0 0px 1000px rgba(255, 255, 255, 0.95) inset;
  box-shadow: 0 0 0px 1000px rgba(255, 255, 255, 0.95) inset;
  transition: background-color 5000s ease-in-out 0s;
}
</style>

